import React, { useState, useRef, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import { logout } from "../../redux/features/authSlice";
import EmptyModal from "./EmptyModal";

const ROUTE_TITLES = {
   dashboard: { title: "Dashboard", subtitle: "Overview of your logistics operations" },
   user: { title: "User Master", subtitle: "Manage system users" },
   "fleet-master": { title: "Fleet Master", subtitle: "Manage fleet records" },
   "driver-master": { title: "Driver Master", subtitle: "Manage driver records" },
   "helper-master": { title: "Helper Master", subtitle: "Manage helper records" },
   "customer-master": { title: "Customer Master", subtitle: "Manage customer records" },
   "vendor-master": { title: "Vendor Master", subtitle: "Manage vendor records" },
   "lane-master": { title: "Lane Master", subtitle: "Manage lane records" },
   "contractor-master": { title: "Contractor Master", subtitle: "Manage contractor records" },
   "location-master": { title: "Location Master", subtitle: "Manage location records" },
   trip: { title: "Trip", subtitle: "Manage trip operations" },
   "adhoc-lane": { title: "Adhoc Lane", subtitle: "Manage adhoc lanes" },
   "adhoc-vendor": { title: "Adhoc Vendor", subtitle: "Manage adhoc vendors" },
   profile: { title: "Profile", subtitle: "Your account details" },
};

const ACTION_TITLES = {
   add: "Add",
   "add-form": "Add",
   "add-user": "Add User",
   list: "List",
   view: "View",
   edit: "Edit",
   contractual: "Contractual Trip",
   adhoc: "Adhoc Trip",
};

const getPageMeta = (pathname) => {
   const segments = pathname.split("/").filter(Boolean);

   if (segments.length === 0 || segments[0] === "dashboard") {
      return ROUTE_TITLES.dashboard;
   }

   const moduleKey = segments[0];
   const moduleMeta = ROUTE_TITLES[moduleKey];

   const meaningfulSegment = [...segments]
      .reverse()
      .find((segment) => !/^\d+$/.test(segment) && segment !== moduleKey);

   if (moduleMeta && meaningfulSegment && ACTION_TITLES[meaningfulSegment]) {
      return {
         title: `${ACTION_TITLES[meaningfulSegment]} ${moduleMeta.title}`,
         subtitle: moduleMeta.subtitle,
      };
   }

   if (moduleMeta) {
      return moduleMeta;
   }

   const fallback = segments.find((segment) => !/^\d+$/.test(segment)) || "Home";

   return {
      title: fallback.replace(/-/g, " ").replace(/\b\w/g, (char) => char.toUpperCase()),
      subtitle: "Manage your workspace",
   };
};

const Header = () => {
   const location = useLocation();
   const dispatch = useDispatch();
   const profileRef = useRef(null);

   const [isOpen, setIsOpen] = useState(false);
   const [profileOpen, setProfileOpen] = useState(false);
   const [searchQuery, setSearchQuery] = useState("");

   const role_name = useSelector((state) => state?.auth?.user?.data?.user?.role_name);
   const user_name = useSelector(
      (state) =>
         state?.auth?.user?.data?.user?.name ||
         state?.auth?.user?.data?.user?.full_name ||
         "Corporate Admin"
   );

   const userInitials = user_name
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase())
      .join("");

   const pageMeta = getPageMeta(location.pathname);

   useEffect(() => {
      const handleClickOutside = (event) => {
         if (profileRef.current && !profileRef.current.contains(event.target)) {
            setProfileOpen(false);
         }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
   }, []);

   const handleLogout = () => {
      dispatch(logout());
   };

   return (
      <>
         <nav className="main-header navbar navbar-expand navbar-white navbar-light wis-topbar">
            <div className="wis-topbar-left">
               <button
                  type="button"
                  className="wis-topbar-toggle"
                  data-widget="pushmenu"
                  aria-label="Toggle sidebar"
               >
                  <i className="fas fa-bars" />
               </button>
               <div className="wis-topbar-title-wrap">
                  <h1 className="wis-topbar-title">{pageMeta.title}</h1>
                  <p className="wis-topbar-subtitle d-none d-sm-block">{pageMeta.subtitle}</p>
               </div>
            </div>

            <div className="wis-topbar-center d-none d-md-flex">
               <div className="wis-topbar-search">
                  <i className="fas fa-search wis-topbar-search-icon" />
                  <input
                     type="search"
                     className="wis-topbar-search-input"
                     placeholder="Search here..."
                     value={searchQuery}
                     onChange={(event) => setSearchQuery(event.target.value)}
                     aria-label="Search"
                  />
               </div>
            </div>

            <div className="wis-topbar-right">
               <div className="wis-topbar-search d-md-none">
                  <i className="fas fa-search wis-topbar-search-icon" />
                  <input
                     type="search"
                     className="wis-topbar-search-input"
                     placeholder="Search..."
                     value={searchQuery}
                     onChange={(event) => setSearchQuery(event.target.value)}
                     aria-label="Search"
                  />
               </div>

               <button type="button" className="wis-topbar-icon-btn" aria-label="Notifications">
                  <i className="far fa-bell" />
                  <span className="wis-topbar-badge">3</span>
               </button>

               <div className="wis-topbar-profile-wrap" ref={profileRef}>
                  <button
                     type="button"
                     className="wis-topbar-profile"
                     onClick={() => setProfileOpen((prev) => !prev)}
                     aria-expanded={profileOpen}
                     aria-label="User menu"
                  >
                     <div className="wis-topbar-profile-avatar">{userInitials || "CA"}</div>
                     <div className="wis-topbar-profile-text d-none d-md-flex">
                        <span className="wis-topbar-profile-name">{user_name}</span>
                        <span className="wis-topbar-profile-role">{role_name || "User"}</span>
                     </div>
                     <i className="fas fa-chevron-down wis-topbar-profile-caret d-none d-md-inline" />
                  </button>

                  {profileOpen && (
                     <div className="wis-topbar-profile-menu">
                        <button
                           type="button"
                           className="wis-topbar-profile-menu-item"
                           onClick={() => {
                              setProfileOpen(false);
                              setIsOpen(true);
                           }}
                        >
                           <i className="fas fa-sign-out-alt" />
                           Logout
                        </button>
                     </div>
                  )}
               </div>
            </div>
         </nav>

         <EmptyModal showModal={isOpen} handleCloseModal={() => setIsOpen(false)}>
            <div className="d-flex flex-column align-items-center pb-3">
               <p className="h3">Are you sure you want to logout?</p>
               <div className="d-flex mt-3">
                  <button
                     className="btn btn-danger mr-3"
                     style={{ width: "120px" }}
                     onClick={handleLogout}
                  >
                     Yes
                  </button>
                  <button
                     className="btn btn-success"
                     style={{ width: "120px" }}
                     onClick={() => setIsOpen(false)}
                  >
                     No
                  </button>
               </div>
            </div>
         </EmptyModal>
      </>
   );
};

export default Header;
